import { NotificationComponentConfig } from '@apis/Notification/model';
import { Space } from '@mantine/core';
import { ChartSettingsSwitch, ChartTypePicker } from '@root/Components/DashboardLayout/Charts/ChartDashboardItemEditor';
import { ChartConfig } from '@root/Components/DashboardLayout/Charts/ChartRenderer';
import { StandaloneChartEditor } from '@root/Components/DashboardLayout/Charts/Models';
import { createDatasourceSchemaContext, DatasourceSchemaContext, DatasourceSchemaCtxProvider } from '@root/Components/Filter/DatasourceContext';
import { SettingsSectionItem, SettingsSectionItemBody, SettingsSectionItemHeader, SettingsSectionItemHeaderLabel } from '@root/Design/Settings';
import { QueryDatasource } from '@root/Services/Query/QueryDatasource';
import { SchemaService } from '@root/Services/QueryExpr';
import { useEffect, useMemo, useRef } from 'react';

interface IChartSettingsInputPropsSchemaCtx {
    schemaCtx: DatasourceSchemaContext;
}
interface IChartSettingsInputPropsDsSchema {
    queryDatasource: QueryDatasource;
    schemaSvc: SchemaService;
}
interface IBaseChartSettingsInputProps {
    hideFilter?: boolean;
    value: NotificationComponentConfig;
    onChange: (value: NotificationComponentConfig) => void;
}
type IChartSettingsInputProps = IBaseChartSettingsInputProps & (IChartSettingsInputPropsSchemaCtx | IChartSettingsInputPropsDsSchema);
type NotificationChartConfig = NotificationComponentConfig & ChartConfig;
export function ChartSettingsInput(props: IChartSettingsInputProps) {
    const { hideFilter, value, onChange, ...rest } = props;
    const chartConfig = value as NotificationChartConfig;
    const changeHandler = useRef(onChange);
    const schemaCtx = useMemo(() => {
        if ('schemaCtx' in rest) {
            return rest.schemaCtx;
        } else {
            return createDatasourceSchemaContext(rest.schemaSvc, rest.queryDatasource);
        }
    }, Object.values(rest));

    const chartEditor = useMemo(() => {
        const chartModel = {
            settings: chartConfig,
            getDatasource: () => schemaCtx.datasource,
            updateSettings: () => {},
        };
        const chartEditor = StandaloneChartEditor.create(chartModel, schemaCtx);
        chartEditor.hideFilter = !!hideFilter;

        return chartEditor;
    }, [schemaCtx, chartConfig.chartType, hideFilter]);

    useEffect(() => {
        const { dispose } = chartEditor.onSettingsChanged.listen(() => {
            changeHandler.current?.(chartEditor.settings as unknown as NotificationComponentConfig);
        });
        return dispose;
    }, [chartEditor.onSettingsChanged]);

    return (
        <>
            <SettingsSectionItem>
                <SettingsSectionItemHeader>
                    <SettingsSectionItemHeaderLabel>Content Type</SettingsSectionItemHeaderLabel>
                </SettingsSectionItemHeader>
                <SettingsSectionItemBody>
                    <Space h="md" />
                    <ChartTypePicker editor={chartEditor} selectionLabel="content" />
                </SettingsSectionItemBody>
            </SettingsSectionItem>
            <ChartSettingsSwitch editor={chartEditor} />
        </>
    );
}

ChartSettingsInput.buildForDatasource = (schemaCtx: DatasourceSchemaContext, hideFilter?: boolean) => {
    return function DsBoundChartSettingsInput(props: { value: NotificationComponentConfig; onChange: (value: NotificationComponentConfig) => void }) {
        return (
            <DatasourceSchemaCtxProvider schemaCtx={schemaCtx}>
                <ChartSettingsInput hideFilter={hideFilter} schemaCtx={schemaCtx} {...props} />
            </DatasourceSchemaCtxProvider>
        );
    };
};
